/**
 * Rule presets (§4.1). Presets are plain data: adding a league preset = adding one object
 * to PRESETS. They are deep-frozen; use cloneRules() or createCustomRules() to edit.
 */
import type { RuleSet } from './ruleset';
import { cloneRules } from './ruleset';

function freezeRules(rules: RuleSet): RuleSet {
  Object.freeze(rules.timeouts);
  Object.freeze(rules.shotClock);
  if (rules.breaks) Object.freeze(rules.breaks);
  if (rules.clockStops) Object.freeze(rules.clockStops);
  Object.freeze(rules.teamFoulExcludedTypes);
  Object.freeze(rules.personalFoulExcludedTypes);
  Object.freeze(rules.ejectionFoulTypes);
  for (const c of rules.ejectionCombos) {
    Object.freeze(c.types);
    Object.freeze(c);
  }
  Object.freeze(rules.ejectionCombos);
  return Object.freeze(rules);
}

export const FIBA_RULES: RuleSet = freezeRules({
  id: 'fiba',
  periods: 4,
  periodLengthSec: 10 * 60,
  overtimeLengthSec: 5 * 60,
  teamFoulPenaltyAt: 5,
  overtimeTeamFoulPenaltyAt: 5,
  overtimeFoulsCarryOver: true,
  personalFoulLimit: 5,
  // FIBA: every player foul (incl. player technicals) is a team foul.
  teamFoulExcludedTypes: [],
  personalFoulExcludedTypes: [],
  ejectionFoulTypes: ['disqualifying'],
  // Art. 41.4: two technicals, two flagrants, or one of each. Disruptive fouls do not count.
  ejectionCombos: [{ types: ['technical', 'flagrant'], count: 2 }],
  // Art. 18.2.5: at most 2 when the clock shows 2:00 or less in Q4.
  timeouts: { kind: 'perHalf', firstHalf: 2, secondHalf: 3, perOvertime: 1, lastTwoMinutesMax: 2 },
  shotClock: { enabled: true, fullSec: 24, resetSec: 14, resetOnScore: true, holdAfterScore: true },
  // Field goals stop the clock in the last 2:00 of Q4 and overtime.
  clockStops: { afterBasketOtherPeriodsSec: 0, afterBasketLastPeriodsSec: 120, onFoul: true },
  // Official FIBA: 1 min timeout, 2 min between quarters and before overtime, 15 min half-time.
  breaks: { timeoutSec: 60, periodBreakSec: 120, halfTimeSec: 900, overtimeBreakSec: 120 },
});

export const PRESETS: Readonly<Record<'fiba', RuleSet>> = Object.freeze({
  fiba: FIBA_RULES,
});

export type PresetId = keyof typeof PRESETS;

export const PRESET_IDS: readonly PresetId[] = ['fiba'];

/** Returns a mutable copy of a preset, or undefined for an unknown id. */
export function getPreset(id: string): RuleSet | undefined {
  if (id === 'fiba') return cloneRules(PRESETS[id]);
  return undefined;
}
